import { useRef, useEffect, useState, useCallback } from "react";
import { Eraser, Check, Loader2 } from "lucide-react";

export const SignaturePad = ({ onSave, onCancel, saving }) => {
  const canvasRef = useRef(null);
  const drawing = useRef(false);
  const isEmpty = useRef(true);
  const [empty, setEmpty] = useState(true);

  const prime = useCallback(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ratio = window.devicePixelRatio || 1;
    const rect = canvas.getBoundingClientRect();
    if (rect.width === 0) return;
    canvas.width = rect.width * ratio;
    canvas.height = rect.height * ratio;
    const ctx = canvas.getContext("2d");
    ctx.setTransform(ratio, 0, 0, ratio, 0, 0);
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, rect.width, rect.height);
    ctx.lineWidth = 2.6;
    ctx.lineCap = "round";
    ctx.lineJoin = "round";
    ctx.strokeStyle = "#0a0a0a";
  }, []);

  useEffect(() => {
    prime();
    // Re-prime on resize/orientation change ONLY when the pad is still empty,
    // so the mobile address-bar show/hide (which fires resize) never wipes an
    // in-progress signature.
    const onResize = () => { if (isEmpty.current) prime(); };
    window.addEventListener("resize", onResize);
    window.addEventListener("orientationchange", onResize);
    return () => {
      window.removeEventListener("resize", onResize);
      window.removeEventListener("orientationchange", onResize);
    };
  }, [prime]);

  const getPos = (e) => {
    const rect = canvasRef.current.getBoundingClientRect();
    return { x: e.clientX - rect.left, y: e.clientY - rect.top };
  };

  const start = (e) => {
    e.preventDefault();
    drawing.current = true;
    try { canvasRef.current.setPointerCapture(e.pointerId); } catch (_) {}
    const ctx = canvasRef.current.getContext("2d");
    const p = getPos(e);
    ctx.beginPath();
    ctx.moveTo(p.x, p.y);
  };

  const move = (e) => {
    if (!drawing.current) return;
    e.preventDefault();
    const ctx = canvasRef.current.getContext("2d");
    const p = getPos(e);
    ctx.lineTo(p.x, p.y);
    ctx.stroke();
    if (isEmpty.current) { isEmpty.current = false; setEmpty(false); }
  };

  const end = (e) => {
    if (!drawing.current) return;
    drawing.current = false;
    try { canvasRef.current.releasePointerCapture(e.pointerId); } catch (_) {}
  };

  const clear = () => { prime(); isEmpty.current = true; setEmpty(true); };

  const handleSave = () => {
    if (empty) return;
    onSave(canvasRef.current.toDataURL("image/png"));
  };

  return (
    <div data-testid="signature-pad">
      <div className="rounded-xl border-2 border-dashed border-white/20 bg-white overflow-hidden">
        <canvas
          ref={canvasRef}
          className="w-full h-48 touch-none cursor-crosshair block select-none"
          style={{ touchAction: "none" }}
          onPointerDown={start}
          onPointerMove={move}
          onPointerUp={end}
          onPointerLeave={end}
          onPointerCancel={end}
          data-testid="signature-canvas"
        />
      </div>
      <p className="text-xs text-zinc-500 mt-2">Podepište se prstem nebo myší do bílého pole.</p>
      <div className="flex items-center gap-3 mt-4">
        <button
          type="button"
          onClick={clear}
          className="inline-flex items-center gap-2 text-sm text-zinc-400 hover:text-white px-3 py-2"
          data-testid="signature-clear-btn"
        >
          <Eraser className="w-4 h-4" /> Vymazat
        </button>
        <div className="flex-1" />
        {onCancel && (
          <button type="button" onClick={onCancel} className="text-sm text-zinc-400 hover:text-white px-3 py-2" data-testid="signature-cancel-btn">
            Zrušit
          </button>
        )}
        <button
          type="button"
          onClick={handleSave}
          disabled={empty || saving}
          className="btn-accent inline-flex items-center gap-2 text-sm disabled:opacity-50"
          data-testid="signature-save-btn"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />} Podepsat
        </button>
      </div>
    </div>
  );
};
